import { DateTimeFormat } from '@billy/design-system'
import { ChangeEvent, ReactElement, useMemo } from 'react'

import { BookkeepingItem as BookkeepingItemType } from '@features/reconciliation/types/bookkeepingItem'

import { useEmberRouteToUrlGenerator } from '../../../../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useOrganizationViewUrl } from '../../../../../../../../hooks/routing/useOrganizationViewUrl'
import { getCurrentLocale } from '../../../../../../../../utils'
import { isEmberRoute } from '../../../../../../../../utils/routing/isEmberRoute'
import { useBankTransactionDrawer } from '../../../../contexts'
import { bookkeepingItemToIcon } from '../../consts/bookkeepingItemToIcon'
import { getBookkeepingItemRoute } from '../../utils/getBookkeepingItemRoute'
import { getDisplayAmount } from '../../utils/getDisplayAmount'
import { ListItem, ListItemProps } from '../ListElements/ListItem/ListItem'

interface BookkeepingItemProps extends Omit<ListItemProps, 'description' | 'displayAmount' | 'date' | 'icon'> {
  bookkeepingItem: BookkeepingItemType
  onCheckedChange?: (checked: boolean) => void
  footerContent?: ReactElement
}

export const BookkeepingItem = ({
  bookkeepingItem,
  isOutlined = false,
  checked = false,
  onCheckedChange,
  showCheckbox = false,
  footerContent,
}: BookkeepingItemProps): ReactElement => {
  const { description, amount, date, type, currency: itemCurrencyId, exchangeRate } = bookkeepingItem
  const { currency: accountCurrencyId } = useBankTransactionDrawer()
  const { generateUrl } = useEmberRouteToUrlGenerator()

  const route = getBookkeepingItemRoute(bookkeepingItem)
  const { url: reactUrl } = useOrganizationViewUrl(route)

  const icon = bookkeepingItemToIcon[type]
  const locale = getCurrentLocale()
  const formattedDate = DateTimeFormat.getFormattedDate(date, locale)

  const displayAmount = useMemo(
    () => getDisplayAmount({ amount, itemCurrencyId, accountCurrencyId, exchangeRate }),
    [amount, itemCurrencyId, accountCurrencyId, exchangeRate],
  )

  const itemUrl = useMemo(() => {
    if (isEmberRoute(route)) {
      const linkId = bookkeepingItem.transactionId || bookkeepingItem.id
      return generateUrl ? generateUrl(route, linkId) : ''
    }
    return reactUrl || ''
  }, [route, generateUrl, reactUrl, bookkeepingItem.id, bookkeepingItem.transactionId])

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    onCheckedChange?.(event.target.checked)
  }

  return (
    <ListItem
      description={description}
      displayAmount={displayAmount}
      date={formattedDate}
      isOutlined={isOutlined}
      checked={checked}
      handleCheckboxChange={handleCheckboxChange}
      icon={icon}
      showCheckbox={showCheckbox}
      viewUrl={itemUrl}
      footerContent={footerContent}
    />
  )
}
